import { Button, Group, Image, Text, Stack, ActionIcon } from "@mantine/core";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { UploadFiles } from "../../hooks/usePerformRequest";

interface FileUploaderProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  error?: React.ReactNode;
}

export const FileUploader = ({
  value,
  onChange,
  onFileSelect,
  label,
  description,
  placeholder,
  error,
}: FileUploaderProps & { onFileSelect?: (file: File) => void }) => {
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const handleFileChange = async (file: File | null) => {
    if (!file) return;

    if (onFileSelect) {
      onFileSelect(file);
      return;
    }

    setLocalPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const results = await UploadFiles([file]);
      if (results && results[0] && results[0].data?.data?.url) {
        onChange(results[0].data.data.url);
      }
    } catch (e) {
      console.error(e);
      setLocalPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const previewUrl = localPreview || (value && typeof value === "string" ? value : "");

  return (
    <Stack gap="xs">
      <Stack gap={0}>
        {label && (
          <Text size="sm" fw={500}>
            {label}
          </Text>
        )}
        {description && (
          <Text size="xs" color="dimmed">
            {description}
          </Text>
        )}
      </Stack>

      {previewUrl ? (
        <div style={{ position: "relative", width: "fit-content" }}>
          <Image src={previewUrl} w={200} h={120} radius="md" fit="cover" />
          <ActionIcon
            color="red"
            variant="filled"
            size="sm"
            style={{ position: "absolute", top: 5, right: 5 }}
            onClick={() => {
              setLocalPreview(null);
              onChange("");
            }}
          >
            <IconX size={14} />
          </ActionIcon>
        </div>
      ) : (
        <Group>
          <label style={{ cursor: "pointer" }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              disabled={uploading}
            />
            <Button
              component="span"
              loading={uploading}
              leftSection={<IconUpload size={16} />}
              variant="light"
            >
              {placeholder || "Upload Image"}
            </Button>
          </label>
        </Group>
      )}

      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};
